import api from '../lib/api';

export interface Project {
  id: number;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectDto {
  name: string;
  description?: string;
}

export interface UpdateProjectDto {
  name?: string;
  description?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ApiResponse<T> {
  status: 'success' | 'error';
  data?: T;
  message?: string;
  code?: number;
  pagination?: PaginationMeta;
}

export interface GetProjectsResponse extends ApiResponse<Project[]> {
  pagination: PaginationMeta;
}

export interface GetProjectResponse extends ApiResponse<Project> {}

export interface CreateProjectResponse extends ApiResponse<Project> {}

export interface UpdateProjectResponse extends ApiResponse<Project> {}

export interface DeleteProjectResponse extends ApiResponse<void> {}

export interface DuplicateProjectResponse extends ApiResponse<Project> {}

class ProjectsService {
  private readonly baseUrl = '/api/projects';

  async getProjects(params?: PaginationParams): Promise<GetProjectsResponse> {
    const response = await api.get<GetProjectsResponse>(this.baseUrl, {
      params: {
        page: params?.page || 1,
        limit: params?.limit || 10
      }
    });
    return response.data;
  }

  async getProject(id: number): Promise<GetProjectResponse> {
    const response = await api.get<GetProjectResponse>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async createProject(data: CreateProjectDto): Promise<CreateProjectResponse> {
    const response = await api.post<CreateProjectResponse>(this.baseUrl, data);
    return response.data;
  }

  async updateProject(id: number, data: UpdateProjectDto): Promise<UpdateProjectResponse> {
    const response = await api.put<UpdateProjectResponse>(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  async deleteProject(id: number): Promise<DeleteProjectResponse> {
    const response = await api.delete<DeleteProjectResponse>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async duplicateProject(id: number): Promise<DuplicateProjectResponse> {
    const response = await api.post<DuplicateProjectResponse>(`${this.baseUrl}/${id}/duplicate`);
    return response.data;
  }
}

export default new ProjectsService();